'use client'

import { type RefObject, useLayoutEffect } from 'react'

/**
 * The custom property every sticky app header is offset by (#14653).
 * `globals.css` declares the `0px` default on `:root`.
 */
export const FEEDBACK_BANNER_HEIGHT_VAR = '--feedback-banner-height'

/**
 * Publishes the feedback-mode banner's MEASURED height as
 * `--feedback-banner-height` on `document.documentElement` (#14653).
 *
 * The banner is `sticky top-0`, and so is every app header shell. Without an
 * offset both pin at `top: 0` and the `z-feedback-banner` banner covers the
 * top of the `z-nav-secondary` header. The shells use `APP_HEADER_STICKY_CLASS`
 * (`src/components/app-header-sticky.ts`), which pins them at this variable,
 * and `useHeaderBannerHeight` folds it into `--header-height` so every
 * downstream `sticky top-[var(--header-height)]` follows.
 *
 * Measured rather than constant because the banner's control row wraps at
 * narrow widths — the lesson #13189 taught `--header-height`.
 *
 * `visible` is the banner's own render condition. The component returns
 * `null` while still MOUNTED (Done, a dismissed outcome), so unmount alone is
 * not a sufficient exit; and an inline value on `:root` outlives a client-side
 * navigation, so every exit removes the property rather than writing `0px`.
 *
 * `useLayoutEffect`, not `useEffect`: the banner is sticky from its first
 * paint, so publishing after paint leaves a frame where it covers the header.
 */
export function useFeedbackBannerHeight(
  bannerRef: RefObject<HTMLElement | null>,
  visible: boolean,
) {
  useLayoutEffect(() => {
    const root = document.documentElement
    const clear = () => root.style.removeProperty(FEEDBACK_BANNER_HEIGHT_VAR)

    const banner = bannerRef.current
    if (!visible || !banner) {
      clear()
      return
    }

    // Border-box height from `getBoundingClientRect`, not `contentRect`:
    // the banner's padding is part of what the header has to clear.
    const publish = () => {
      const height = banner.getBoundingClientRect().height
      if (height > 0) {
        root.style.setProperty(FEEDBACK_BANNER_HEIGHT_VAR, `${height}px`)
      } else {
        clear()
      }
    }

    publish()
    if (typeof ResizeObserver === 'undefined') return clear

    const observer = new ResizeObserver(publish)
    observer.observe(banner)
    return () => {
      observer.disconnect()
      clear()
    }
  }, [bannerRef, visible])
}
